import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';

interface PhaseNodeData {
  label: string;
  type: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'error';
  progress?: number;
}

const PhaseNode: React.FC<NodeProps<PhaseNodeData>> = ({ data, isConnectable }) => {
  const getStatusColor = () => {
    switch (data.status) {
      case 'not_started': return 'bg-gray-500';
      case 'in_progress': return 'bg-blue-500';
      case 'completed': return 'bg-green-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusLabel = () => {
    switch (data.status) {
      case 'not_started': return 'Not Started';
      case 'in_progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'error': return 'Error';
      default: return 'Unknown';
    }
  };

  return (
    <div className="phase-node bg-white rounded-lg shadow-lg border border-gray-200 p-3 min-w-[160px]">
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        className="w-3 h-3"
      />
      
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-gray-900 truncate">{data.label}</h3>
        <span className={`inline-block w-3 h-3 rounded-full ${getStatusColor()}`}></span>
      </div>
      
      <div className="text-xs text-gray-500 mb-2">
        Type: {data.type}
      </div>
      
      <div className="text-xs text-gray-600">
        Status: {getStatusLabel()}
      </div>
      
      {data.status === 'in_progress' && typeof data.progress === 'number' && (
        <div className="mt-2">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-blue-500 h-1.5 rounded-full" 
              style={{ width: `${Math.min(Math.max(data.progress, 0), 100)}%` }}
            ></div>
          </div>
        </div>
      )}
      
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
        className="w-3 h-3"
      />
    </div>
  );
};

export default memo(PhaseNode);
